import type {InventoryMenuOptions} from '../sm-inventory-context-menu/sm-inventory-context-menu.component';
import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild} from '@angular/core';
import {buildListItemDescriptionPreview} from '../../text/list-item-description';
import {SmContextMenuItemModel} from '../sm-context-menu-item/sm-context-menu-item.component';
import {SmImagePreviewComponent} from '../sm-image-preview/sm-image-preview.component';

import {SmItemListItemModel} from './SmItemListItemModel';
import {Observable} from 'rxjs';
import {PhotoGalleryKind} from '../sm-photo-gallery/photo-gallery-kind';
export type {SmItemListItemModel} from './SmItemListItemModel';

@Component({
  selector: 'sm-item-list-item',
  templateUrl: './sm-item-list-item.component.html',
  styleUrls: ['./sm-item-list-item.component.scss'],
  standalone: false,
})
export class SmItemListItemComponent implements OnChanges {
  private static readonly dragThresholdPx = 6;

  @Input({required: true}) item!: SmItemListItemModel;
  @Input() galleryImages: string[] | null = null;
  @Input() photoSourceKind: PhotoGalleryKind = 'item';
  @Input() photoSourceId: string | null = null;
  @Input() loadGalleryImages: (() => Observable<string[]>) | null = null;
  @Input() inventoryMenu: InventoryMenuOptions | null = null;
  @Input() menuItems: SmContextMenuItemModel[] = [];
  @Input() emptyDescriptionLabel = '';
  @Input() searchQuery = '';
  @Input() menuAriaLabel = '';
  @Input() dragEnabled = false;
  @Input() touchDragFromMainSurface = false;
  @Input() touchDragHoldDelayMs = 0;
  @Input() interactive = true;
  @Input() surfaceActionLabel = '';
  @Input() titleRoute: string | null = null;
  @Input() showKindLabel = true;
  @Input() imageLongPressEnabled = false;
  @Input() imageActionLabel = '';
  @Input() imageBadgeText = '';

  @Output() open = new EventEmitter<SmItemListItemModel>();
  @Output() imageAction = new EventEmitter<void>();
  @Output() menuItemSelect = new EventEmitter<SmContextMenuItemModel>();
  @Output() dragPointerDown = new EventEmitter<PointerEvent>();
  @Output() dragPointerMove = new EventEmitter<PointerEvent>();
  @Output() dragPointerEnd = new EventEmitter<PointerEvent>();
  @Output() dragPointerCancel = new EventEmitter<PointerEvent>();

  @ViewChild(SmImagePreviewComponent) private readonly imagePreview?: SmImagePreviewComponent;

  private dragPointerId: number | null = null;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragStartedAt = 0;
  private suppressNextOpen = false;
  private imageLoadFailed = false;

  ngOnChanges(changes: SimpleChanges): void {
    const itemChange = changes['item'];
    if (itemChange && itemChange.previousValue?.imageUrl !== itemChange.currentValue?.imageUrl) this.imageLoadFailed = false;
  }

  get description(): string {
    return buildListItemDescriptionPreview(this.item.description);
  }

  get hasImage(): boolean {
    return Boolean(this.item.imageUrl) && !this.imageLoadFailed;
  }

  get fallbackIconName(): string {
    return this.item.fallbackIconName || 'cube-outline';
  }

  get isDisabled(): boolean {
    return Boolean(this.item.disabled);
  }

  get quantityLabel(): string | null {
    if (typeof this.item.quantity !== 'number') {
      return null;
    }

    return this.item.unitLabel ? `${this.item.quantity} ${this.item.unitLabel}` : `x${this.item.quantity}`;
  }

  handleOpen(): void {
    if (this.interactive || this.surfaceActionLabel) this.open.emit(this.item);
  }

  handleImageError(): void { this.imageLoadFailed = true; }

  handleMainClick(event: MouseEvent): void {
    if (this.suppressNextOpen) {
      event.preventDefault();
      event.stopPropagation();
      this.suppressNextOpen = false;
      return;
    }

    this.handleOpen();
  }

  handleMenuItemSelect(item: SmContextMenuItemModel): void {
    this.menuItemSelect.emit(item);
  }

  handleContextMenu(event: MouseEvent): void {
    if (this.dragEnabled && this.touchDragFromMainSurface) {
      event.preventDefault();
    }
  }

  handleDragPointerDown(event: PointerEvent): void {
    if (!this.dragEnabled || !this.canStartDrag(event)) {
      return;
    }

    event.stopPropagation();
    this.dragPointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.dragStartedAt = Date.now();
    this.suppressNextOpen = false;
    this.dragPointerDown.emit(event);
  }

  handleDragPointerMove(event: PointerEvent): void {
    if (!this.dragEnabled || this.dragPointerId !== event.pointerId) {
      return;
    }

    const distance = Math.hypot(event.clientX - this.dragStartX, event.clientY - this.dragStartY);
    if (distance >= SmItemListItemComponent.dragThresholdPx) {
      this.imagePreview?.closePreview();
      this.suppressNextOpen = true;
    }

    this.dragPointerMove.emit(event);
  }

  handleDragPointerEnd(event: PointerEvent): void {
    if (!this.dragEnabled || this.dragPointerId !== event.pointerId) {
      return;
    }

    if (
      event.pointerType === 'touch'
      && this.touchDragHoldDelayMs > 0
      && Date.now() - this.dragStartedAt >= this.touchDragHoldDelayMs
    ) {
      this.suppressNextOpen = true;
    }

    this.dragPointerEnd.emit(event);
    this.dragPointerId = null;
  }

  handleDragPointerCancel(event: PointerEvent): void {
    if (this.dragPointerId !== event.pointerId) {
      return;
    }

    this.dragPointerCancel.emit(event);
    this.dragPointerId = null;
    this.suppressNextOpen = false;
  }

  preventNativeDrag(event: DragEvent): void {
    event.preventDefault();
  }

  private canStartDrag(event: PointerEvent): boolean {
    if (event.pointerType !== 'touch') {
      return true;
    }

    if (this.touchDragFromMainSurface) {
      return true;
    }

    return event.target instanceof Element && Boolean(
      event.target.closest('.sm-item-list-item__media--touch-drag-source')
    );
  }
}
